// client/src/pages/Admin/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import authService from '../../services/authService';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h1`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 30px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #4a5568;
  font-size: 14px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  padding-right: ${props => props.hasIcon ? '50px' : '20px'};
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: #f7fafc;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #667eea;
    outline: none;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
    background: white;
    transform: translateY(-2px);
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    color: #667eea;
    background: rgba(102, 126, 234, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  background-color: #fed7d7;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  background-color: #c6f6d5;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #9ae6b4;
`;

const BrandLoginLink = styled.div`
  text-align: center;
  margin-top: 20px;

  a {
    color: #667eea;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TestCredentialsSection = styled.div`
  margin-top: 20px;
  text-align: center;
  padding: 16px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const TestButton = styled.button`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin: 4px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(72, 187, 120, 0.3);
  }

  &.direct-login {
    background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
    
    &:hover {
      box-shadow: 0 4px 8px rgba(237, 137, 54, 0.3);
    }
  }
`;

const CredentialsDisplay = styled.div`
  font-size: 12px;
  color: #4a5568;
  margin-top: 8px;
  font-family: 'Courier New', Courier, monospace;
  background: white;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
`;

const DebugSection = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #edf2f7;
  border-radius: 6px;
  font-size: 11px;
  font-family: 'Courier New', Courier, monospace;
  color: #4a5568;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #cbd5e0;
`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);

  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl') || '/admin-dhobicart';

  const addDebugLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, `[${timestamp}] ${message}`].slice(-20));
  };

  useEffect(() => {
    addDebugLog('Admin Login component loaded');
    // Check if already authenticated
    if (authService.isAuthenticated('admin')) {
      addDebugLog('Already authenticated, redirecting...');
      navigate('/admin-dhobicart');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    addDebugLog(`Attempting login with email: ${formData.email}`);

    try {
      const result = await authService.adminLogin(formData);
      addDebugLog('AuthService login successful');
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/admin-dhobicart');
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Login failed';
      addDebugLog(`Login failed: ${errorMsg}`);
      setError(errorMsg);
      console.error('Admin login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fillTestCredentials = () => {
    setFormData({
      email: 'admin@dhobicart.com',
      password: 'admin123'
    });
    addDebugLog('Test credentials filled');
    setSuccess('Test credentials filled! Click "Access Dashboard" to login.');
  };

  const tryDirectLogin = async () => {
    setLoading(true);
    setError('');
    addDebugLog('Attempting direct login...');
    
    try {
      const response = await fetch(API_URL2 + '/api/auth/direct-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'admin@dhobicart.com',
          password: 'admin123'
        })
      });

      addDebugLog(`Direct login response status: ${response.status}`);
      const data = await response.json();
      addDebugLog(`Direct login response: ${JSON.stringify(data)}`);
      
      if (data.success) {
        // Store the token
        localStorage.setItem('adminToken', data.token);
        addDebugLog('Token stored successfully');
        setSuccess('Direct login successful! Redirecting...');
        setTimeout(() => {
          navigate('/admin-dhobicart');
        }, 1000);
      } else {
        const errorMsg = data.message || 'Direct login failed';
        addDebugLog(`Direct login failed: ${errorMsg}`);
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Direct login failed: ' + err.message;
      addDebugLog(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const testAuthService = async () => {
    addDebugLog('Testing authService...');
    try {
      addDebugLog(`authService.adminLogin exists: ${!!authService.adminLogin}`);
      addDebugLog(`authService.isAuthenticated exists: ${!!authService.isAuthenticated}`);
      
      const isAuth = authService.isAuthenticated('admin');
      addDebugLog(`Current auth status: ${isAuth}`);
      
    } catch (err) {
      addDebugLog(`AuthService test error: ${err.message}`);
    }
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <Logo>DhobiCart Admin</Logo>
        <Title>Admin Portal</Title>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <FormGroup>
          <Label htmlFor="email">Admin Email</Label>
          <InputContainer>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter admin email"
              required
            />
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <InputContainer>
            <Input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              hasIcon={true}
              required
            />
            <EyeButton
              type="button"
              onClick={togglePasswordVisibility}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "🙈" : "👁️"}
            </EyeButton>
          </InputContainer>
        </FormGroup>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? 'Logging in...' : 'Access Dashboard'}
        </SubmitButton>

        <BrandLoginLink>
          <a href="/">Brand Login</a>
        </BrandLoginLink>
      </LoginForm>
    </LoginContainer>
  );
};

export default AdminLogin;
